import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/utils/supabase';
import { Container, Logo } from '@beyond-silence/ui';
import { DashboardIcon, ArticlesIcon, SettingsIcon, TeamIcon } from '@/components/icons';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Profile = Record<string, any> & { id: string; display_name: string | null; avatar_url: string | null; role: string; };

/**
 * Staff CMS layout with sidebar navigation.
 * Responsive: sidebar collapses to top bar on mobile, full sidebar on desktop.
 * Shows logged-in user's avatar, name, and role at the sidebar bottom.
 */
export function StaffLayout() {
  const { t } = useTranslation('staff');
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [session, setSession] = useState<boolean | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileVersion, setProfileVersion] = useState(0);
  const refreshProfile = () => setProfileVersion(v => v + 1);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(!!data.session);
      if (!data.session) navigate('/login', { replace: true });
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(!!session);
      if (!session) navigate('/login', { replace: true });
    });

    return () => listener.subscription.unsubscribe();
  }, [navigate]);

  // Fetch profile when session is confirmed (or after settings save)
  useEffect(() => {
    if (!session) return;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: prof } = await (supabase.from('profiles') as any)
        .select('*')
        .eq('id', user.id)
        .single();
      if (prof) setProfile(prof);
    })();
  }, [session, profileVersion]);

  if (session === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg">
        <div className="font-ui text-ui-base text-ink-light">Loading…</div>
      </div>
    );
  }

  if (!session) return null;

  const navItems = [
    { label: t('nav.dashboard'), href: '/dashboard', Icon: DashboardIcon },
    { label: t('nav.articles'),  href: '/articles',  Icon: ArticlesIcon },
    { label: t('nav.settings'),  href: '/settings',  Icon: SettingsIcon },
  ];

  // Admin-only: Team management
  if (profile?.role === 'admin') {
    navItems.push({ label: 'Team', href: '/team', Icon: TeamIcon });
  }

  const isActive = (href: string) => location.pathname.startsWith(href);

  const roleLabel = (role: string) => {
    const map: Record<string, string> = { reader: 'Reader', author: 'Author', editor: 'Editor', admin: 'Admin' };
    return map[role] ?? role;
  };

  const roleBadge = (role: string) => {
    const map: Record<string, string> = {
      admin: 'bg-primary text-white',
      editor: 'bg-highlight/60 text-ink',
      author: 'bg-soft/60 text-ink',
      reader: 'bg-gray-200 text-gray-600',
    };
    return map[role] ?? 'bg-gray-200 text-gray-600';
  };

  const avatarFallback = profile?.display_name
    ? profile.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div className="flex min-h-screen bg-bg">
      {/* ── Mobile top bar ──────────────────────────────────── */}
      <div className="fixed inset-x-0 top-0 z-40 border-b border-soft/40 bg-white px-4 py-3 md:hidden">
        <div className="flex items-center justify-between">
          <span className="font-ui text-ui-base font-semibold text-primary">BS CMS</span>
          <button onClick={() => setSidebarOpen((o) => !o)} className="rounded-md p-1.5 hover:bg-soft/50" aria-label="Toggle menu">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d={sidebarOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Sidebar overlay ─────────────────────────────────── */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-ink/50 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ── Sidebar ─────────────────────────────────────────── */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 border-r border-soft/40 bg-white transition-transform duration-200 md:static md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`.trim().replace(/\s+/g, ' ')}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="border-b border-soft/40 px-5 py-4">
            <Logo href="/dashboard" size="lg" />
          </div>

          {/* Nav items */}
          <nav className="flex-1 space-y-1 px-3 py-4">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} onClick={(e) => { e.preventDefault(); navigate(item.href); setSidebarOpen(false); }}
                className={`flex items-center gap-3 rounded-md px-3 py-2.5 font-ui text-ui-sm font-medium transition-colors ${isActive(item.href) ? 'bg-primary/10 text-primary' : 'text-ink-light hover:bg-soft/30 hover:text-ink'}`.trim().replace(/\s+/g, ' ')}>
                <item.Icon className="h-5 w-5" />
                {item.label}
              </a>
            ))}
          </nav>

          {/* ── User section at bottom ─────────────────────── */}
          <div className="border-t border-soft/40 p-3">
            <div className="flex items-center gap-3">
              {/* Avatar */}
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" loading="lazy" decoding="async" className="h-9 w-9 rounded-full object-cover ring-2 ring-soft/40" />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/20 font-ui text-ui-sm font-bold text-secondary">
                  {avatarFallback}
                </div>
              )}

              {/* Name + role */}
              <div className="min-w-0 flex-1">
                <p className="truncate font-ui text-ui-sm font-semibold text-ink">
                  {profile?.display_name ?? 'Staff'}
                </p>
                <span className={`inline-block rounded-pill px-2 py-0 text-ui-xs font-medium ${roleBadge(profile?.role ?? 'reader')}`}>
                  {roleLabel(profile?.role ?? 'reader')}
                </span>
              </div>

              {/* Logout */}
              <button
                onClick={async () => { await supabase.auth.signOut(); navigate('/login'); }}
                className="rounded-md p-1.5 text-ink-light transition-colors hover:bg-soft/30 hover:text-primary"
                title={t('nav.logout')}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────────── */}
      <main className="flex-1 pt-14 md:pt-0">
        <Outlet context={{ refreshProfile }} />
      </main>
    </div>
  );
}
